/**
 * "This week's reading" — turns the patient's analytics into one editorial
 * headline plus a row of flag chips. Deterministic templates over the
 * statistics (no LLM call): only FDR-significant trends, early-warning
 * flags, and Tang-DeRubeis shifts may appear, so every word on screen is
 * backed by a test that survived correction.
 */
import type {
  TrendResult,
  EarlyWarningResult,
  AssessmentPoint,
} from "./chartSpecs";

export type DigestChipKind = "up" | "down" | "warning" | "gain" | "loss";

export interface DigestChip {
  kind: DigestChipKind;
  /** topic or instrument name */
  label: string;
  /** extra detail for tooltip (e.g. slope, q) */
  detail: string;
}

export interface DigestHeadline {
  /** i18n key under digest.* */
  key:
    | "headlineBoth"
    | "headlineRising"
    | "headlineFalling"
    | "headlineWarning"
    | "headlineGain"
    | "headlineLoss"
    | "headlineStable"
    | "headlineNoData";
  params: Record<string, string>;
}

export interface Digest {
  headline: DigestHeadline;
  chips: DigestChip[];
}

const MAX_CHIPS = 6;

export function buildDigest(
  trends: TrendResult[],
  warnings: EarlyWarningResult[],
  assessmentPoints: AssessmentPoint[],
  hasAnyData: boolean
): Digest {
  const sigTrends = trends.filter((t) => t.significant);
  const rising = sigTrends.filter((t) => t.slopePerWeek > 0);
  const falling = sigTrends.filter((t) => t.slopePerWeek < 0);
  const activeWarnings = warnings.filter((w) => w.warning);

  // most recent sudden shift per instrument (latest administration wins)
  const recentShifts = assessmentPoints
    .filter((p) => p.suddenShift)
    .slice(-2);

  // ---- chips: strongest first, capped ----
  const chips: DigestChip[] = [];
  for (const w of activeWarnings) {
    chips.push({
      kind: "warning",
      label: w.topic,
      detail: `AR1 z=${w.ar1Z}, var z=${w.varZ} (p<.10 both)`,
    });
  }
  for (const p of recentShifts) {
    chips.push({
      kind: p.suddenShift === "gain" ? "gain" : "loss",
      label: p.instrument,
      detail: `Δ=${p.delta} (Tang-DeRubeis)`,
    });
  }
  const bySlope = [...sigTrends].sort(
    (a, b) => Math.abs(b.slopePerWeek) - Math.abs(a.slopePerWeek)
  );
  for (const t of bySlope) {
    if (chips.length >= MAX_CHIPS) break;
    chips.push({
      kind: t.slopePerWeek > 0 ? "up" : "down",
      label: t.topic,
      detail: `${t.slopePerWeek > 0 ? "+" : ""}${t.slopePerWeek}/week, q=${t.q}, n=${t.n}`,
    });
  }

  // ---- headline: priority order ----
  // sudden loss > early warning > both-mover > sudden gain > single mover > stable
  const loss = recentShifts.find((p) => p.suddenShift === "loss");
  if (loss) {
    return {
      headline: { key: "headlineLoss", params: { instrument: loss.instrument } },
      chips,
    };
  }
  if (activeWarnings.length > 0) {
    return {
      headline: { key: "headlineWarning", params: { topic: activeWarnings[0].topic } },
      chips,
    };
  }

  const topRising = [...rising].sort((a, b) => b.slopePerWeek - a.slopePerWeek)[0];
  const topFalling = [...falling].sort((a, b) => a.slopePerWeek - b.slopePerWeek)[0];

  if (topRising && topFalling) {
    return {
      headline: {
        key: "headlineBoth",
        params: { falling: topFalling.topic, rising: topRising.topic },
      },
      chips,
    };
  }

  const gain = recentShifts.find((p) => p.suddenShift === "gain");
  if (gain) {
    return {
      headline: { key: "headlineGain", params: { instrument: gain.instrument } },
      chips,
    };
  }

  if (topRising) {
    return {
      headline: { key: "headlineRising", params: { rising: topRising.topic } },
      chips,
    };
  }
  if (topFalling) {
    return {
      headline: { key: "headlineFalling", params: { falling: topFalling.topic } },
      chips,
    };
  }

  return {
    headline: { key: hasAnyData ? "headlineStable" : "headlineNoData", params: {} },
    chips,
  };
}
